import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import FareFamilySelectModal from 'components/Flights/V2/FareFamily/FareFamilySelectModal'
import { type TransportOption, isLeFlightSavedItemData } from 'tripPlanner/api/types'
import type { ModalContextType } from 'contexts/ModalContext'
import { parseSearchParams } from './flightShortlistUtils'

interface UseBookResult {
  bookFlight: (option: TransportOption) => void
  isBooking: boolean
}

interface FareFamilyModalResult {
  fareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
  addToUserCart?: boolean
}

function useBookShortlistedFlight(showModal: ModalContextType): UseBookResult {
  const [isBooking, setIsBooking] = useState(false)
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const region = useAppSelector(state => state.geo.currentRegionCode)

  const bookFlight = useCallback(async(option: TransportOption) => {
    if (!isLeFlightSavedItemData(option.savedItemData)) return

    const data = option.savedItemData
    const params = parseSearchParams(data.searchParams)
    if (!params) {
      showSnackbar(intl.formatMessage({
        id: 'R6wd52-oZj65JhpjjL7G',
        defaultMessage: 'Unable to book this flight. Search parameters are missing.',
        description: 'Error snackbar shown when a shortlisted flight is missing search parameters needed for booking',
      }), 'critical')
      return
    }

    setIsBooking(true)

    try {
      const searchResult = await searchFlights({
        itinerary: params.itinerary,
        currency,
        occupancies: params.occupancies,
        fareClass: params.fareClass,
        region,
        fareType: params.fareType,
      })

      const returnLeg = option.returnLeg
      const returnLegData = isLeFlightSavedItemData(returnLeg?.savedItemData) ? returnLeg.savedItemData : undefined
      const isReturn = !!returnLeg && !!returnLegData
      const fareType = params.fareType === 'return' ? 'return' as const : 'oneWay' as const

      let matchedDepartingJourney: App.JourneyV2 | undefined
      let matchedReturningJourney: App.JourneyV2 | undefined

      const flightUnavailableMessage = intl.formatMessage({
        id: 'hqp07ZRkaLAescD38LIo',
        defaultMessage: 'This flight is no longer available. Please search for new flights.',
        description: 'Warning snackbar shown when a shortlisted flight cannot be found in current search results',
      })

      if (isReturn) {
        const departingFares = searchResult.onewayFares[0] ?? []
        const returningFares = searchResult.onewayFares[1] ?? []

        matchedDepartingJourney = data.journeyKey
          ? departingFares.find(j => j.bookingInfo.journeyKey === data.journeyKey)
          : undefined

        matchedReturningJourney = returnLegData.journeyKey
          ? returningFares.find(j => j.bookingInfo.journeyKey === returnLegData.journeyKey)
          : undefined

        if (!matchedDepartingJourney || !matchedReturningJourney) {
          showSnackbar(flightUnavailableMessage, 'warning')
          setIsBooking(false)
          return
        }
      } else {
        const allFares = searchResult.onewayFares.flat()
        matchedDepartingJourney = data.journeyKey
          ? allFares.find(j => j.bookingInfo.journeyKey === data.journeyKey)
          : undefined

        if (!matchedDepartingJourney) {
          showSnackbar(flightUnavailableMessage, 'warning')
          setIsBooking(false)
          return
        }
      }

      const occupants = params.occupancies[0] ?? { adults: 1 }
      const passengers = generatePassengersFromOccupants(occupants)
      const searchExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

      let departingFareFamily: App.FlightFareFamily | undefined
      let returningFareFamily: App.FlightFareFamily | undefined

      if (isReturn) {
        if (matchedDepartingJourney.fareFamily) {
          const departResult = await showModal<FareFamilyModalResult>(
            React.createElement(FareFamilySelectModal, {
              journey: matchedDepartingJourney,
              occupancies: occupants,
              searchId: searchResult.searchId!,
              sessionId: searchResult.sessionId,
              relativePrice: matchedDepartingJourney.price.all.totalFare,
              isStandalone: true,
            }),
          )

          if (!departResult) {
            setIsBooking(false)
            return
          }
          departingFareFamily = departResult.fareFamily
        }

        if (matchedReturningJourney!.fareFamily) {
          const returnResult = await showModal<FareFamilyModalResult>(
            React.createElement(FareFamilySelectModal, {
              journey: matchedReturningJourney!,
              departingJourney: matchedDepartingJourney,
              departingFareFamily,
              occupancies: occupants,
              searchId: searchResult.searchId!,
              sessionId: searchResult.sessionId,
              relativePrice: matchedReturningJourney!.price.all.totalFare,
              isStandalone: true,
            }),
          )

          if (!returnResult) {
            setIsBooking(false)
            return
          }
          returningFareFamily = returnResult.fareFamily
        }
      } else if (matchedDepartingJourney.fareFamily) {
        const result = await showModal<FareFamilyModalResult>(
          React.createElement(FareFamilySelectModal, {
            journey: matchedDepartingJourney,
            occupancies: occupants,
            searchId: searchResult.searchId!,
            sessionId: searchResult.sessionId,
            relativePrice: matchedDepartingJourney.price.all.totalFare,
            isStandalone: true,
          }),
        )

        if (!result) {
          setIsBooking(false)
          return
        }
        departingFareFamily = result.fareFamily
      }

      const flights = isReturn
        ? [matchedDepartingJourney, matchedReturningJourney!]
        : [matchedDepartingJourney]

      const fareFamilies = isReturn
        ? [departingFareFamily, returningFareFamily]
        : [departingFareFamily]

      const checkoutItem = generateFlightCheckoutItem({
        originAirportCode: params.itinerary[0].departureAirport,
        destinationAirportCode: params.itinerary[0].arrivalAirport,
        occupants,
        passengers,
        fareType,
        viewType: fareType === 'oneWay' ? 'OneWay' : 'TwoOneWays',
        searchId: searchResult.searchId,
        flights,
        baggage: [],
        fareFamilies,
        bundledItemIds: [],
        expiry: searchExpiry,
      })

      dispatch(addToCheckoutCart([checkoutItem]))
      dispatch(navigateToCheckout())
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'Xi8uEjkdq0mQ8_7rn8QW',
        defaultMessage: 'Something went wrong while preparing your booking. Please try again.',
        description: 'Error snackbar shown when booking a shortlisted flight fails unexpectedly',
      }), 'critical')
    } finally {
      setIsBooking(false)
    }
  }, [dispatch, currency, region, showModal, intl])

  return { bookFlight, isBooking }
}

export default useBookShortlistedFlight
